/**
 * «Bu sertifikanın LinkedIn paylaşım görseli» — the one data path the panel uses.
 *
 * ONE SOURCE, TWO ENTRANCES, exactly as the certificate's e-mail already works: the preparation
 * wizard's step and the editor's dialog open the same arrangement through the same port and render
 * the same component. There is no browser-only copy of a layout — what a screen shows came from
 * the server and what it saves goes back to it.
 *
 * The port is small on purpose — read, save, reset, preview. None of them posts to LinkedIn, and
 * none of them touches the certificate: the drawing they edit sits beside the template, outside
 * the release the certificate is produced from.
 */
import { createContext, useContext } from 'react';
import type { ApiClient } from '../../api/client';

export type CardTextAlign = 'left' | 'center' | 'right';

export interface CardArtworkBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CardTextItem {
  kind: 'text';
  id: string;
  x: number;
  y: number;
  width: number;
  text: string;
  fontSize: number;
  color: string;
  bold: boolean;
  align: CardTextAlign;
}

export interface CardImageItem {
  kind: 'image';
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  /** An asset in the product's own library. The layout stores the reference, never the bytes. */
  assetId: string;
}

export type CardItem = CardTextItem | CardImageItem;

/** The editable layout, in the card's own coordinates. */
export interface LinkedInCardDesign {
  version: 1;
  background: string;
  /** Null when the author took the certificate off the card entirely. */
  artwork: CardArtworkBox | null;
  /** Drawn in this order, over the background and over the certificate. */
  items: CardItem[];
}

export interface LinkedInCardView {
  templateId: string;
  design: LinkedInCardDesign;
  /** False while this certificate still uses the automatic card. */
  custom: boolean;
  /** What «varsayılan otomatik görsele dön» goes back to. */
  standard: LinkedInCardDesign;
  canvas: { width: number; height: number };
  updatedAt: string | null;
}

export interface LinkedInCardSaved extends LinkedInCardView {
  /** Overlays whose asset is no longer in the library, so the panel can name them. */
  missingAssets: string[];
}

export interface LinkedInCardPreview {
  /** `data:image/png;base64,…` at exactly the card size. */
  imageDataUri: string;
  width: number;
  height: number;
  missingAssets: string[];
  /** False when the certificate could not be rendered into the sample. */
  artworkIncluded: boolean;
  /** Why it could not — null when it is there, or when the author took it off themselves. */
  artworkProblem: { code: string; message: string } | null;
}

/**
 * The language a share image belongs to.
 *
 * A certificate sealed in two languages has two share images, because the picture embeds that
 * language's certificate and the post beside it is in that language. Optional on every call: left
 * out, the server answers for the template's own declared language, which is what every caller
 * before locales meant and what a single-language template still means.
 */
export type CardLocale = 'tr' | 'en';

export interface TemplateLinkedInCardPort {
  read(templateId: string, lang?: CardLocale): Promise<LinkedInCardView>;
  save(templateId: string, design: LinkedInCardDesign, lang?: CardLocale): Promise<LinkedInCardSaved>;
  /** «Varsayılan otomatik görsele dön» — the arrangement goes, the automatic card returns. */
  reset(templateId: string, lang?: CardLocale): Promise<LinkedInCardView>;
  /** The same composer the share uses, over this arrangement. Posts nothing. */
  preview(templateId: string, design: LinkedInCardDesign, lang?: CardLocale): Promise<LinkedInCardPreview>;
}

/** The product's own path: four routes under the template the author is already allowed to edit. */
export function apiTemplateLinkedInCard(api: ApiClient): TemplateLinkedInCardPort {
  const path = (templateId: string, lang?: CardLocale) =>
    `/api/v1/internal/templates/${encodeURIComponent(templateId)}/linkedin-card${lang ? `?lang=${lang}` : ''}`;
  return {
    read: (templateId, lang) => api.json<LinkedInCardView>(path(templateId, lang)),
    save: (templateId, design, lang) =>
      api.json<LinkedInCardSaved>(path(templateId, lang), { method: 'PATCH', body: { design } }),
    reset: (templateId, lang) => api.json<LinkedInCardView>(path(templateId, lang), { method: 'DELETE' }),
    preview: (templateId, design, lang) =>
      api.json<LinkedInCardPreview>(
        `/api/v1/internal/templates/${encodeURIComponent(templateId)}/linkedin-card/preview${lang ? `?lang=${lang}` : ''}`,
        { method: 'POST', body: { design } },
      ),
  };
}

const Ctx = createContext<TemplateLinkedInCardPort | null>(null);

export const TemplateLinkedInCardProvider = Ctx.Provider;

/**
 * The port, or null where none is mounted.
 *
 * Null rather than a throw and null rather than a silent local store: the frozen prototype surface
 * has no server behind it, and a panel that quietly edited a browser-only copy of a real
 * certificate's share image would be lying about what it saved.
 */
export function useTemplateLinkedInCard(): TemplateLinkedInCardPort | null {
  return useContext(Ctx);
}

/** A new item's id. Short, stable and never taken from anything a recipient owns. */
export function newItemId(prefix: 'text' | 'image'): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}
